import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { STORE, STORE_FILTER } from "@/constants/testIds";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const catSlug = (c) => c.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Store = () => {
  const { addToCart } = useApp();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [selectedCats, setSelectedCats] = useState([]);

  useEffect(() => {
    // Mount-only bootstrap: fetch static lists once.
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/meta/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const toggleCat = (c) => {
    setSelectedCats((cs) => cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]);
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return products.filter((p) => {
      if (selectedCats.length && !selectedCats.includes(p.category)) return false;
      if (query && !(`${p.name} ${p.description} ${p.category}`.toLowerCase().includes(query))) return false;
      return true;
    });
  }, [products, q, selectedCats]);

  const handleAdd = (p) => {
    addToCart(p);
    toast.success(`${p.name} added to basket`);
  };

  const clearFilters = () => { setQ(""); setSelectedCats([]); };
  const hasFilters = q.length > 0 || selectedCats.length > 0;

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-12">
        <div>
          <p className="text-xs uppercase tracking-label text-secondary">Pooja Samagri</p>
          <h1 className="display-serif text-5xl md:text-6xl mt-3">Sacred goods, honestly sourced.</h1>
        </div>
        <p className="max-w-md text-sm text-muted-foreground">
          Every item is bought directly from artisan families across Bharat. No middlemen. No compromise on purity.
        </p>
      </div>

      {/* Filter bar */}
      <div className="mb-10 space-y-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
          <Input
            data-testid={STORE_FILTER.searchInput}
            placeholder="Search for diya, agarbatti, mala…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 rounded-none border-0 border-b bg-transparent"
          />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={STORE_FILTER.categoryChip(c)}
              onClick={() => toggleCat(c)}
              className={`px-3.5 py-1.5 text-[11px] uppercase tracking-label border luxe-btn ${
                selectedCats.includes(c) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-secondary"
              }`}
            >
              {c}
            </button>
          ))}
          {hasFilters && (
            <button
              data-testid={STORE_FILTER.clearFilters}
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-label ml-2 text-muted-foreground hover:text-primary"
            >
              <X className="w-3 h-3" strokeWidth={1.5} /> Clear
            </button>
          )}
        </div>
        <p className="text-[11px] uppercase tracking-label text-muted-foreground">
          {filtered.length} of {products.length} items
        </p>
      </div>

      <div data-testid={STORE.grid} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {filtered.map((p) => (
          <div
            key={p.sku}
            data-testid={STORE.productCard(p.sku)}
            className="group editorial-card flex flex-col overflow-hidden hover:border-primary transition-colors"
          >
            <Link to={`/product/${p.sku}`} className="relative aspect-[4/5] overflow-hidden bg-muted">
              <img
                src={p.image}
                alt={p.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-4 left-4">
                <span className="text-[10px] uppercase tracking-label bg-background/90 backdrop-blur px-2 py-1">{p.category}</span>
              </div>
            </Link>
            <div className="p-6 flex flex-col flex-1">
              <Link to={`/product/${p.sku}`} className="font-serif text-xl leading-snug hover:text-primary">{p.name}</Link>
              <p className="text-sm text-muted-foreground mt-2 flex-1 line-clamp-3">{p.description}</p>
              <div className="mt-6 flex items-center justify-between">
                <span className="font-serif text-2xl">${p.price.toFixed(2)}</span>
                <button
                  onClick={() => handleAdd(p)}
                  data-testid={STORE.addToCartBtn(p.sku)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground text-[11px] uppercase tracking-label font-bold hover:bg-primary/90 luxe-btn"
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Add
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Store;
